import { useState } from "react";
import { GitBranchPlusIcon, PlusIcon, Trash2Icon } from "lucide-react";

import {
  useEscalationMatrix,
  useCreateEscalationEntry,
  useUpdateEscalationEntry,
  useDeleteEscalationEntry,
  type EscalationMatrixEntry,
  type EscalationMatrixInput,
} from "@/api/escalation";
import { useUsers } from "@/api/users";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/auth";
import type { UserDetail } from "@/types";

const SEVERITIES = ["critical", "warning", "info"];

function UserSelect({
  value,
  onChange,
  users,
}: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  users: UserDetail[];
}) {
  return (
    <select
      className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">— none —</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>
          {u.full_name || u.email}
        </option>
      ))}
    </select>
  );
}

type EscalationDraft = {
  l1_user_id?: string | null;
  l2_user_id?: string | null;
  l3_user_id?: string | null;
  l1_timer_minutes?: number | null;
  l2_timer_minutes?: number | null;
  l3_timer_minutes?: number | null;
};

// A32 — an escalation row must name at least one responder, and any STAFFED level must
// have a positive timer. Blocks saving no-responder / zero-timer rows.
function escalationErrors(v: EscalationDraft): string[] {
  const errs: string[] = [];
  if (!v.l1_user_id && !v.l2_user_id && !v.l3_user_id) {
    errs.push("Assign at least one responder (L1, L2, or L3).");
  }
  for (const [lvl, uid, t] of [
    ["L1", v.l1_user_id, v.l1_timer_minutes],
    ["L2", v.l2_user_id, v.l2_timer_minutes],
    ["L3", v.l3_user_id, v.l3_timer_minutes],
  ] as const) {
    if (uid && !(typeof t === "number" && Number.isFinite(t) && t > 0)) {
      errs.push(`${lvl} timer must be at least 1 minute.`);
    }
  }
  return errs;
}

function MatrixRow({
  entry,
  users,
}: {
  entry: EscalationMatrixEntry;
  users: UserDetail[];
}) {
  const update = useUpdateEscalationEntry(entry.id);
  const del = useDeleteEscalationEntry();
  const [draft, setDraft] = useState<EscalationMatrixEntry>(entry);
  const dirty = JSON.stringify(draft) !== JSON.stringify(entry);
  const rowErrs = escalationErrors(draft);

  return (
    <tr className="border-b last:border-0">
      <td className="px-2 py-2 font-mono text-xs">{entry.alert_type}</td>
      <td className="px-2 py-2 text-xs">{entry.alert_severity}</td>
      {(["l1_user_id", "l2_user_id", "l3_user_id"] as const).map((f) => (
        <td key={f} className="px-2 py-2">
          <UserSelect
            value={draft[f]}
            users={users}
            onChange={(v) => setDraft((d) => ({ ...d, [f]: v }))}
          />
        </td>
      ))}
      {(["l1_timer_minutes", "l2_timer_minutes", "l3_timer_minutes"] as const).map(
        (f) => (
          <td key={f} className="px-2 py-2">
            <Input
              type="number"
              className="h-9 w-16 text-xs"
              value={draft[f]}
              onChange={(e) =>
                setDraft((d) => ({ ...d, [f]: Number(e.target.value) }))
              }
            />
          </td>
        ),
      )}
      <td className="px-2 py-2">
        <div className="flex gap-1">
          <Button
            size="sm"
            disabled={!dirty || rowErrs.length > 0 || update.isPending}
            title={rowErrs.join(" ")}
            onClick={() =>
              update.mutate({
                l1_user_id: draft.l1_user_id,
                l2_user_id: draft.l2_user_id,
                l3_user_id: draft.l3_user_id,
                l1_timer_minutes: draft.l1_timer_minutes,
                l2_timer_minutes: draft.l2_timer_minutes,
                l3_timer_minutes: draft.l3_timer_minutes,
              })
            }
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!confirm("Delete this escalation entry?")) return;
              await del.mutateAsync(entry.id);
            }}
          >
            <Trash2Icon className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function AddEntryForm({ users }: { users: UserDetail[] }) {
  const create = useCreateEscalationEntry();
  const [form, setForm] = useState<EscalationMatrixInput>({
    alert_type: "",
    alert_severity: "critical",
    l1_user_id: null,
    l2_user_id: null,
    l3_user_id: null,
    l1_timer_minutes: 5,
    l2_timer_minutes: 15,
    l3_timer_minutes: 30,
  });

  const errs = escalationErrors(form);
  const canSubmit = !!form.alert_type.trim() && errs.length === 0;

  const submit = async () => {
    if (!canSubmit) return;
    await create.mutateAsync({ ...form, alert_type: form.alert_type.trim() });
    setForm((f) => ({ ...f, alert_type: "" }));
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-sm font-semibold">Add escalation entry</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label>Alert type</Label>
            <Input
              value={form.alert_type}
              onChange={(e) => setForm((f) => ({ ...f, alert_type: e.target.value }))}
              placeholder="e.g. abandoned_object"
            />
          </div>
          <div className="space-y-1">
            <Label>Severity</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.alert_severity}
              onChange={(e) =>
                setForm((f) => ({ ...f, alert_severity: e.target.value }))
              }
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {(
            [
              ["L1 user", "l1_user_id", "l1_timer_minutes"],
              ["L2 user", "l2_user_id", "l2_timer_minutes"],
              ["L3 user", "l3_user_id", "l3_timer_minutes"],
            ] as const
          ).map(([label, userField, timerField]) => (
            <div key={userField} className="space-y-1">
              <Label>{label}</Label>
              <UserSelect
                value={form[userField] as string | null}
                users={users}
                onChange={(v) => setForm((f) => ({ ...f, [userField]: v }))}
              />
              <Input
                type="number"
                className="mt-1"
                value={form[timerField] as number}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [timerField]: Number(e.target.value) }))
                }
                placeholder="timer (min)"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-3">
          {form.alert_type.trim() && errs.length > 0 && (
            <p className="text-xs text-yellow-600">{errs.join(" ")}</p>
          )}
          <Button
            disabled={create.isPending || !canSubmit}
            title={errs.join(" ")}
            onClick={submit}
          >
            <PlusIcon className="mr-2 h-4 w-4" /> Add entry
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EscalationMatrix() {
  const role = useAuthStore((s) => s.user?.role);
  // AUDIT D3 — this destructured only `data`, so a failed fetch rendered a
  // bare header with an empty table: indistinguishable from "no escalation
  // rules configured". Those are opposite facts for an operator.
  const { data, isLoading, isError, error, refetch } = useEscalationMatrix();
  const { data: usersData } = useUsers();
  const users = usersData?.items ?? [];

  if (role !== "admin") {
    return <p className="text-sm text-muted-foreground">Admin access required.</p>;
  }

  return (
    <div className="space-y-6">
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
        <GitBranchPlusIcon className="h-6 w-6" />
        Escalation Matrix
      </h1>

      <AddEntryForm users={users} />

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="the escalation matrix"
        onRetry={refetch}
        isEmpty={!!data && data.items.length === 0}
        emptyMessage="No escalation entries yet."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-2 py-3 text-left">Alert type</th>
                <th className="px-2 py-3 text-left">Severity</th>
                <th className="px-2 py-3 text-left">L1</th>
                <th className="px-2 py-3 text-left">L2</th>
                <th className="px-2 py-3 text-left">L3</th>
                <th className="px-2 py-3 text-left">L1 min</th>
                <th className="px-2 py-3 text-left">L2 min</th>
                <th className="px-2 py-3 text-left">L3 min</th>
                <th className="px-2 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((entry) => (
                <MatrixRow key={entry.id} entry={entry} users={users} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
